import React from "react";

import DashboardNavigation from "../../DashboardNavigation/DashboardNavigation";
import ScrollIndicator from "../../Home/ScrollIndicator";
import useScrollToTop from "../../../hooks/useScollToTop";

import TechniqueCard from "./components/TechniqueCard";
import TechniqueDetailModal from "./components/TechniqueDetailModal";
import TechniqueQuizPanel from "./components/TechniqueQuizPanel";
import TechniquesEmptyState from "./components/TechniquesEmptyState";
import TechniquesHero from "./components/TechniquesHero";
import TechniquesSkeleton from "./components/TechniquesSkeleton";
import TechniquesStats from "./components/TechniquesStats";
import TechniquesToolbar from "./components/TechniquesToolbar";
import Uv2DrawPanel from "./components/Uv2DrawPanel";

import { useTechniquesRevisionPage } from "./hooks/useTechniquesRevisionPage";
import { getTechniqueKey } from "./utils/techniquesRevisionUtils";

function TechniquesRevisionPage() {
  useScrollToTop();

  const {
    techniques,
    filteredTechniques,
    selectedTechnique,
    favoriteIds,
    filters,
    loading,
    refreshing,
    favoritesLoading,
    stats,
    uv2Fiches,
    fetchTechniques,
    setSelectedTechnique,
    handleFilterChange,
    resetFilters,
    handleToggleFavorite,
    handleRandomUv2Fiche,
  } = useTechniquesRevisionPage();

  const isQuizMode = filters.mode === "quiz";
  const isUv2Mode = filters.mode === "uv2";
  const isUv2WaitingForFiche = isUv2Mode && filters.uv2 === "all";

  return (
    <main className="min-h-screen bg-slate-50 pb-24 pt-[88px] text-slate-950 dark:bg-slate-950 dark:text-white lg:pt-[104px]">
      <TechniquesHero stats={stats} />

      <section className="mx-auto -mt-12 w-full max-w-[1280px] px-4 sm:px-6 lg:px-8">
        <div className="rounded-[2rem] border border-white/80 bg-white/95 p-4 shadow-2xl shadow-slate-200/70 backdrop-blur-xl dark:border-white/10 dark:bg-slate-900/95 dark:shadow-black/30 sm:p-6">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-sky-100 px-3 py-2 text-xs font-bold uppercase tracking-[0.16em] text-sky-700 dark:bg-sky-500/10 dark:text-sky-200">
                Révisions techniques
              </div>

              <h1 className="mt-4 font-titre text-3xl font-bold tracking-tight text-slate-950 dark:text-white sm:text-4xl">
                Techniques, UV2, Kata & Quiz
              </h1>

              <p className="mt-3 max-w-3xl text-sm font-medium leading-7 text-slate-600 dark:text-white/60 sm:text-base">
                Une seule page pour réviser les techniques de judo, préparer les
                fiches UV2, consulter les katas et tester ses connaissances avec
                un quiz visuel.
              </p>
            </div>

            <button
              type="button"
              onClick={fetchTechniques}
              disabled={refreshing}
              className="inline-flex min-h-12 items-center justify-center rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-bold text-slate-700 transition hover:-translate-y-0.5 hover:border-sky-200 hover:text-sky-700 disabled:cursor-not-allowed disabled:opacity-60 dark:border-white/10 dark:bg-white/[0.05] dark:text-white"
            >
              {refreshing ? "Actualisation..." : "Actualiser"}
            </button>
          </div>

          <TechniquesStats stats={stats} />
        </div>
      </section>

      <section className="mx-auto mt-6 w-full max-w-[1280px] px-4 sm:px-6 lg:px-8">
        <TechniquesToolbar
          filters={filters}
          uv2Fiches={uv2Fiches}
          resultsCount={filteredTechniques.length}
          totalCount={techniques.length}
          onFilterChange={handleFilterChange}
          onReset={resetFilters}
        />

        {isUv2Mode && (
          <Uv2DrawPanel
            selectedFiche={filters.uv2}
            fiches={uv2Fiches}
            onDraw={handleRandomUv2Fiche}
            onClear={() => handleFilterChange("uv2", "all")}
          />
        )}

        {loading ? (
          <TechniquesSkeleton />
        ) : isQuizMode ? (
          <TechniqueQuizPanel
            techniques={filteredTechniques}
            favoriteIds={favoriteIds}
          />
        ) : isUv2WaitingForFiche ? (
          <div className="mt-6 rounded-[2rem] border border-dashed border-sky-300 bg-white p-8 text-center shadow-xl shadow-slate-200/50 dark:border-sky-300/20 dark:bg-white/[0.05] dark:shadow-black/20">
            <h2 className="font-titre text-2xl font-bold text-slate-950 dark:text-white">
              Choisissez une fiche UV2
            </h2>

            <p className="mx-auto mt-3 max-w-xl text-sm font-medium leading-7 text-slate-500 dark:text-white/50">
              Pour les révisions UV2, sélectionnez une fiche manuellement ou
              lancez un tirage au sort. Une seule fiche sera affichée à la fois.
            </p>
          </div>
        ) : filteredTechniques.length > 0 ? (
          <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {filteredTechniques.map((technique, index) => (
              <TechniqueCard
                key={getTechniqueKey(technique, index)}
                technique={technique}
                isFavorite={favoriteIds.includes(technique.id)}
                favoritesLoading={favoritesLoading}
                onOpen={() => setSelectedTechnique(technique)}
                onToggleFavorite={() => handleToggleFavorite(technique)}
              />
            ))}
          </div>
        ) : (
          <TechniquesEmptyState onReset={resetFilters} />
        )}
      </section>

      <TechniqueDetailModal
        technique={selectedTechnique}
        isFavorite={
          selectedTechnique ? favoriteIds.includes(selectedTechnique.id) : false
        }
        onClose={() => setSelectedTechnique(null)}
        onToggleFavorite={() =>
          selectedTechnique && handleToggleFavorite(selectedTechnique)
        }
      />

      <DashboardNavigation />
      <ScrollIndicator />
    </main>
  );
}

export default TechniquesRevisionPage;
